import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmitterService } from '../../services/emitter.service';
import { NotificationService } from '../../services/notification.service';
import { UnseenNotifierService } from '../../services/unseen-notifier.service';
import { NotificationItem, NotificationResult, NotificationViewMode, UnseenTotal } from '../../interfaces';

type ToastItem = NotificationItem & { _toastKey: number };

@Component({
  selector: 'mtm-toaster',
  templateUrl: './mtm-toaster.component.html',
  styleUrl: './mtm-toaster.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MTMToasterComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);
  private readonly unseenNotifier = inject(UnseenNotifierService);

  readonly NotificationViewType = NotificationViewMode;

  notifications: ToastItem[] = [];

  private seq = 0;
  private lastUnseenTotal: number | null = null;

  ngOnInit(): void {
    EmitterService.get('popup.notification')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload: NotificationItem) => {
        if (!payload) return;
        const prepared = this.notificationService.prepareNotification(payload);
        if (!prepared?.notificationDisplay?.label) {
          prepared.notificationDisplay = { ...(prepared.notificationDisplay ?? {}), label: payload.code };
        }
        this.pushIfNew(prepared);
      });

    EmitterService.get('user.notification')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload: NotificationItem) => {
        if (!payload) return;
        this.pushIfNew(this.notificationService.prepareNotification(payload));
      });

    const sub = this.unseenNotifier.subscribe((data: Record<string, UnseenTotal>) => {
      const total = Object.values(data ?? {}).reduce((s, v) => s + (v?.unseenCount ?? 0), 0);
      if (this.lastUnseenTotal !== null && total > this.lastUnseenTotal) {
        this.fetchAndToastLatest();
      }
      this.lastUnseenTotal = total;
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  removeNotification(notification: ToastItem): void {
    this.notifications = this.notifications.filter((n) => n._toastKey !== notification._toastKey);
    this.cdr.markForCheck();
  }

  private fetchAndToastLatest(): void {
    this.notificationService
      .getNotifications({ limit: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: NotificationResult) => {
        const item = result?.data?.[0];
        if (item) this.pushIfNew(item);
      });
  }

  private pushIfNew(prepared: NotificationItem): void {
    if (!prepared?.notificationDisplay?.label) return;
    if (this.notifications.some((n) => n.time === prepared.time)) return;
    this.notifications = [...this.notifications, { ...prepared, _toastKey: ++this.seq }];
    this.cdr.markForCheck();
  }
}
